import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { IconChevronRight } from '@americanexpress/dls-icons';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import Card from './CenturionNetworkTabLounges';

type FeaturedCard = {
  iata: string;
  title: string;
  subtitle: ReactNode;
  isNewOpening: boolean;
  image: string;
  imageAlt: string;
};

type CenturionNetworkTabContent = {
  navTitle: string;
  navItems: string[];
  loungeSectionTitle: string;
  ctaLabel: string;
  featuredCards: FeaturedCard[];
};

const fallbackContent: CenturionNetworkTabContent = {
  navTitle: 'The Centurion® Network',
  navItems: [
    'About Centurion Network',
    'Access',
    'Digital Waitlist',
    'Culinary Collective',
  ],
  loungeSectionTitle: 'Featured Centurion Lounges',
  ctaLabel: 'View All Centurion Lounges',
  featuredCards: [
    {
      iata: 'LAS',
      title: 'Sidecar by The Centurion Lounge',
      subtitle: 'LAS · Harry Reid International Airport',
      isNewOpening: true,
      image: 'las-centurion-sidecar.jpg',
      imageAlt: 'Sidecar by The Centurion Lounge',
    },
    {
      iata: 'ATL',
      title: 'Atlanta',
      subtitle: 'ATL · Hartsfield–Jackson Atlanta International Airport',
      isNewOpening: false,
      image: 'atl-centurion-lounge.jpg',
      imageAlt: 'Atlanta Centurion Lounge',
    },
    {
      iata: 'SLC',
      title: 'Salt Lake City',
      subtitle: (
        <>
          SLC · Salt Lake City International
          <br /> Airport
        </>
      ),
      isNewOpening: true,
      image: 'slc-centurion-lounge.jpg',
      imageAlt: 'Salt Lake City Centurion Lounge',
    },
  ],
};

export default function CenturionNetwork() {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { header } = useCommonContent();
  const content: CenturionNetworkTabContent =
    header?.centurionNetwork || fallbackContent;

  const { featuredCards } = content;

  return (
    <div className="tw-mb-4 tw-pt-6">
      <div className="tw-mx-auto tw-w-full tw-px-4 md_tw-px-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-10 md_tw-flex-row">
          <div className="tw-w-full md_tw-w-auto md_tw-min-w-80 md_tw-shrink-0">
            <nav>
              <h2 className="tw-whitespace-nowrap tw-font-sans tw-text-base tw-font-bold tw-text-gray-300">
                {content.navTitle}
              </h2>
              <ul className="tw-mt-4 tw-space-y-4 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400">
                {content.navItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="tw-hidden tw-w-px tw-self-stretch tw-bg-blue-deep-tint-700-t12 md_tw-block" />

          <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-1 tw-flex-col">
            <h2 className="tw-mb-4 tw-font-sans tw-text-base tw-font-bold tw-text-gray-300">
              {content.loungeSectionTitle}
            </h2>

            <div className="scrollbar-hide tw-flex tw-w-full tw-gap-4 tw-overflow-x-auto tw-pb-2 md_tw-grid md_tw-grid-cols-3 md_tw-gap-4 md_tw-overflow-visible md_tw-pb-0 lg_tw-gap-8">
              {featuredCards.map(
                ({ iata, title, subtitle, image, imageAlt, isNewOpening }) => (
                  <div
                    key={iata}
                    className="tw-w-64 tw-shrink-0 tw-snap-start md_tw-w-auto md_tw-shrink"
                  >
                    <Card
                      key={iata}
                      title={title}
                      subtitle={subtitle}
                      image={`${baseOneCmsImgUrl}${image.replace(/^\//, '')}`}
                      imageAlt={imageAlt}
                      isNewOpening={isNewOpening}
                    />
                  </div>
                )
              )}
            </div>

            <div className="tw-mt-5 tw-flex tw-items-center tw-gap-1 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400">
              <span>{content.ctaLabel}</span>
              <IconChevronRight
                size="xs"
                isFilled
                color="inherit"
                className="tw-fill-blue-bright-400"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
